import { TRPCPost } from '@/infrastructure/axios';
import { API_PREFIX } from '@/infrastructure/axios/code';
import { logger } from '@/infrastructure/logger';
import { getProductId } from '@/infrastructure/product-info';
import { DataType } from '@/presentation/components/issue-table';
import {
  ICondition,
  IGetTopIssuesReq,
  IGetTopIssuesRsp,
  IssueType,
  ITopIssueInfo,
} from '@/proto/issue';
import {
  MetricSubType,
  MetricType,
  TimeType,
  IFilter,
  IGetDashboardDataRsp,
  IGetDashboardDataReq,
  IGetTopDataReq,
  IGetTopDataRsp,
  IDimensionRecord,
} from '@/proto/metric';
import { toPBTime } from '@raft/utils/src';

export type TopIssueInfo = ITopIssueInfo & DataType;

interface Iparams {
  metricType: MetricType;
  timeWindow?: TimeType;
  filter?: IFilter[];
  totalize?: boolean;
  timeRange?: string[];
  subTypes?: MetricSubType[];
  token?: string;
}

interface TopParams{
  issueType: IssueType,
  condition: ICondition,
  compare?: ICondition,
  sortField?: string,
  sortType?: string
}

export enum MetricName {
  Count='count', // 影响的次数（上报crash等异常的次数）
  UserUv='user_uv', // 影响的用户数（上报crash等异常的用户数）
  DeviceUv='device_uv', // 影响的设备数（上报crash等异常的设备数）
  DauCount='dau_count', // 联网的次数
  DauUserUv='dau_user_uv', // 联网的用户数
  DauDeviceUv='dau_device_uv', // 联网的设备数
  CountRatio= 'count_ratio', // 异常次数率
  DeviceRatio = 'device_uv_ratio', // 设备异常率
  UserUvRatio='user_uv_ratio', // 用户异常率
  avgDeviceUvRatio= 'avg_device_uv_ratio', // 设备平均异常率
  DauLaunchTimes = 'dau_launch_times', // 启动次数

  TrafficExceptionSum = 'traffic_exception_sum' // 异常流量
}


export const GetDashboardData = async ({
  metricType,
  timeWindow,
  filter,
  totalize,
  timeRange,
  subTypes,
  token,
}: Iparams): Promise<IGetDashboardDataRsp> => {
  try {
    const reqBody: IGetDashboardDataReq = {
      productId: getProductId(),
      type: metricType,
      subTypes,
      optionalFilters: filter,
      startTime: timeRange ? toPBTime(timeRange[0]) : undefined,
      endTime: timeRange ? toPBTime(timeRange[1]) : undefined,
      timeWindow,
      totalize,
      token,
    };
    return await TRPCPost<IGetDashboardDataRsp>(
      `/${API_PREFIX}/trpc/trpc.rmonitor.metrics_server.GetMetrics/GetDashboardData`,
      reqBody,
    );
  } catch (e) {
    logger.error('loadDashboard err: ', e);
    return  {
      data: [],
      timePoints: [],
      msg: '',
      code: 0,
    };
  }
};

/**
 * 获取概览排行榜数据 top5
 * @param params
 * @param limit
 */
export const getTopData = async (params: IGetTopDataReq, limit = 5): Promise<{
  records: IDimensionRecord[],
  total: number,
  metricNames: string[]
  deviceTotal: number,
}> => {
  try {
    const reqBody: IGetTopDataReq = {
      productId: getProductId(),
      // top5 固定按天
      timeWindow: TimeType.DAY,
      // 固定5条
      limit,
      ...params,
    };
    const rsp = await TRPCPost<IGetTopDataRsp>(
      `/${API_PREFIX}/trpc/trpc.rmonitor.metrics_server.GetMetrics/GetTopData`,
      reqBody,
    );
    return {
      records: rsp?.data?.records || [],
      total: parseInt(rsp?.total || 0, 10),
      metricNames: rsp?.data?.metricNames ?? [],
      deviceTotal: rsp.deviceUvTotal,
    };
  } catch (e) {
    logger.error('loadDashboard err: ', e);
    return {
      records: [],
      total: 0,
      metricNames: [],
      deviceTotal: 0,
    };
  }
};

/**
 * 获取top20 排行榜数据
 * @param params
 */
export const getTop20Issuelist = async (params: IGetTopIssuesReq): Promise<ITopIssueInfo[]> => {
  try {
    const rsp = await TRPCPost<IGetTopIssuesRsp>(
      `/${API_PREFIX}/trpc/trpc.rmonitor.web_server.IssueList/GetTopIssues`,
      params,
    );
    return (rsp.issues ?? []);
  } catch (e) {
    logger.error('loadDashboard err: ', e);
    // TODO 检查请求出错返回空对象处理逻辑
    return  [];
  }
};

export const GetTop3Issues = async (params: TopParams): Promise<IGetTopIssuesRsp> => {
  try {
    const reqBody: IGetTopIssuesReq = {
      productId: getProductId(),
      issueType: params.issueType,
      condition: params.condition,
      compare: params.compare,
      topNum: 3,
      sortField: params.sortField,
      sortType: params.sortType,
    };
    return await TRPCPost<IGetTopIssuesRsp>(
      `/${API_PREFIX}/trpc/trpc.rmonitor.web_server.IssueList/GetTopIssues`,
      reqBody,
    );
  } catch (e) {
    logger.error('loadDashboard err: ', e);
    // TODO 检查请求出错返回空对象处理逻辑
    return  {
      baseRsp: {},
      issues: [],
    };
  }
};
